import React, { FC, useState, useRef, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { colors } from '../../../theme/palette'
import { Typography } from '../Typography/Typography'
import { ChevronLeft } from '../../../assets/icons'

interface DropdownItem {
    id: string
    label: string
    onClick?: () => void
    href?: string
    color?: string
    icon?: JSX.Element
    disabled?: boolean
}

interface DropdownProps {
    trigger: React.ReactNode
    items: DropdownItem[]
    align?: 'left' | 'right'
    style?: React.CSSProperties
}

const useStyles = createUseStyles({
    container: {
        position: 'relative',
        display: 'inline-block',
    },
    dropdown: {
        position: 'absolute',
        top: '100%',
        left: (props: { align: 'left' | 'right' }) =>
            props.align === 'right' ? 'auto' : '0',
        right: (props: { align: 'left' | 'right' }) =>
            props.align === 'right' ? '0' : 'auto',
        backgroundColor: colors.white,
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        minWidth: '200px',
        maxWidth: '300px',
        maxHeight: '300px',
        overflowY: 'auto',
        zIndex: 2,
        marginTop: '8px',
        display: 'none',
        '&.active': {
            display: 'block',
        },
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        cursor: 'pointer',
        textDecoration: 'none',
        // color: colors.black,
        border: 'none',
        width: '100%',
        textAlign: 'left',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: colors.gray500,
        },
        '&:first-child': {
            borderRadius: '8px 8px 0 0',
        },
        '&:last-child': {
            borderRadius: '0 0 8px 8px',
        },
        '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
    icon: {
        marginRight: '12px',
        display: 'flex',
        alignItems: 'center',
        width: '1.5rem',
    },
    contentWrapper: {
        // Content wrapper styles
    },
})

const Dropdown: FC<DropdownProps> = ({
    trigger,
    items,
    align = 'left',
    style,
}): JSX.Element => {
    const classes = useStyles({ align })
    const [isOpen, setIsOpen] = useState(false)
    const [dropdownPosition, setDropdownPosition] = useState({
        flipVertical: false,
        flipHorizontal: false,
    })
    const dropdownRef = useRef<HTMLDivElement>(null)
    const dropdownContentRef = useRef<HTMLDivElement>(null)

    // Function to calculate optimal dropdown position
    const calculateDropdownPosition = (): void => {
        if (!dropdownRef.current || !dropdownContentRef.current) return

        const triggerRect = dropdownRef.current.getBoundingClientRect()
        const dropdownRect = dropdownContentRef.current.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const { scrollY } = window

        // Check vertical overflow
        const {
            bottom: triggerBottom,
            top: triggerTop,
            left: triggerLeft,
            right: triggerRight,
        } = triggerRect
        const { height: dropdownHeight = 200, width: dropdownWidth } =
            dropdownRect

        const spaceBelow = viewportHeight - (triggerBottom - scrollY)
        const spaceAbove = triggerTop - scrollY

        const flipVertical =
            spaceBelow < dropdownHeight && spaceAbove > spaceBelow

        // Check horizontal overflow
        let flipHorizontal = false
        if (align === 'right') {
            // Check if dropdown overflows left edge
            flipHorizontal = triggerRight - dropdownWidth < 0
        } else {
            // Check if dropdown overflows right edge
            flipHorizontal = triggerLeft + dropdownWidth > viewportWidth
        }

        setDropdownPosition({ flipVertical, flipHorizontal })
    }

    // Calculate position when dropdown opens
    useEffect(() => {
        if (isOpen) {
            // Delay calculation to ensure dropdown is rendered
            setTimeout(calculateDropdownPosition, 0)
        }
    }, [isOpen])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }

        const handleResize = (): void => {
            if (isOpen) {
                calculateDropdownPosition()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        window.addEventListener('resize', handleResize)
        window.addEventListener('scroll', handleResize)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('scroll', handleResize)
        }
    }, [isOpen])

    return (
        <div className={classes.container} ref={dropdownRef}>
            <button
                type="button"
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    e.preventDefault()
                    setIsOpen(!isOpen)
                }}
                style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                }}
            >
                {trigger}
            </button>
            <div
                ref={dropdownContentRef}
                className={`${classes.dropdown} ${classes.contentWrapper} ${
                    isOpen ? 'active' : ''
                }`}
                style={{
                    // Dynamic positioning based on viewport constraints
                    ...(dropdownPosition.flipVertical && {
                        top: 'auto',
                        bottom: '100%',
                        marginTop: '0',
                        marginBottom: '8px',
                    }),
                    ...(dropdownPosition.flipHorizontal &&
                        align === 'left' && {
                            left: 'auto',
                            right: '0',
                        }),
                    ...(dropdownPosition.flipHorizontal &&
                        align === 'right' && {
                            right: 'auto',
                            left: '0',
                        }),
                }}
            >
                {items.map((item) =>
                    item.href ? (
                        <a
                            key={item.id}
                            href={item.href}
                            className={classes.item}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                e.preventDefault()
                                setIsOpen(false)
                            }}
                        >
                            {item.icon && (
                                <span
                                    className={classes.icon}
                                    style={{ color: item.color }}
                                >
                                    {item.icon}
                                </span>
                            )}
                            <Typography size="XXS" color={item.color}>
                                {item.label}
                            </Typography>
                        </a>
                    ) : (
                        <button
                            key={item.id}
                            type="button"
                            className={classes.item}
                            disabled={item.disabled}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                e.preventDefault()
                                item.onClick?.()
                                setIsOpen(false)
                            }}
                        >
                            {item.icon && (
                                <span
                                    className={classes.icon}
                                    style={{ color: item.color }}
                                >
                                    {item.icon}
                                </span>
                            )}
                            <Typography size="XXS" color={item.color}>
                                {item.label}
                            </Typography>
                        </button>
                    )
                )}
            </div>
        </div>
    )
}

export { Dropdown }
